import { useId } from "react";

// Hand-rolled SVG donut. Replaces a Recharts <PieChart> + <Pie> + <Cell>
// composition that was the only Recharts user in the SPA. Three reasons
// to roll our own instead of adding another charting dep:
//
//   1. CSP. Recharts injects `style="..."` attributes on every chart
//      element + animation frame, so the page CSP needed
//      `style-src 'unsafe-inline'`. SVG <path> with `fill` attributes
//      is CSP-clean — no `style` attribute anywhere.
//   2. Bundle. Recharts pulls in 8.5 MB of source (including a d3-* tree)
//      for one donut. Removing it shrinks the dep graph noticeably.
//   3. Surface. We have ONE chart on ONE page. Pulling in a charting
//      library for it is overkill; the arc-path math is ~30 lines.
//
// API mirrors the props we used on Recharts so the call site stays
// almost identical.

export interface DonutSegment {
  /** Used as the React key + tooltip label — must be unique per segment. */
  name: string;
  /** Numeric weight. Zero / negative segments are skipped (no zero-area arc). */
  value: number;
  /** Hex / CSS colour string. Goes straight into the SVG `fill` attribute. */
  color: string;
}

interface DonutChartProps {
  data: DonutSegment[];
  /** Outer radius in viewBox units. Default 55 (matches our previous Recharts use). */
  outerRadius?: number;
  /** Inner radius. Anything < outerRadius makes a donut; 0 makes a pie. */
  innerRadius?: number;
  /** Gap between segments, in radians. 2 in viewBox-px ≈ 0.04 rad on r=55. */
  paddingAngle?: number;
  /** Colour for the all-zero "empty" placeholder ring. */
  emptyColor?: string;
}

const TAU = Math.PI * 2;

/**
 * Path string for one donut segment between startAngle and endAngle.
 * Angles are in radians, measured clockwise from 12-o'clock (negative
 * y-axis), to match what Recharts / d3 default to so the visual layout
 * doesn't change.
 */
function arcPath(cx: number, cy: number, rOuter: number, rInner: number, startAngle: number, endAngle: number): string {
  // Segment goes the long way around when sweep is more than half the circle.
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  // Cartesian points on the outer + inner ring at start and end angles.
  // We use sin/-cos because angle 0 is at 12 o'clock (negative y axis).
  const x1 = cx + rOuter * Math.sin(startAngle);
  const y1 = cy - rOuter * Math.cos(startAngle);
  const x2 = cx + rOuter * Math.sin(endAngle);
  const y2 = cy - rOuter * Math.cos(endAngle);
  const x3 = cx + rInner * Math.sin(endAngle);
  const y3 = cy - rInner * Math.cos(endAngle);
  const x4 = cx + rInner * Math.sin(startAngle);
  const y4 = cy - rInner * Math.cos(startAngle);
  // M = moveTo, A = elliptical arc, L = lineTo, Z = closePath.
  // Sweep flag: 1 (clockwise) for outer arc, 0 (counter-clockwise) for inner.
  return [
    `M ${x1.toFixed(4)} ${y1.toFixed(4)}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${x2.toFixed(4)} ${y2.toFixed(4)}`,
    `L ${x3.toFixed(4)} ${y3.toFixed(4)}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${x4.toFixed(4)} ${y4.toFixed(4)}`,
    "Z",
  ].join(" ");
}

/**
 * Full-ring path. Used when one segment has 100% of the value (a single
 * arc going all the way round can't be expressed as one A command —
 * SVG arcs of >= 360° collapse to a zero-length path). We compose two
 * 180° arcs back to back instead.
 */
function fullRingPath(cx: number, cy: number, rOuter: number, rInner: number): string {
  // Outer ring: start at top, sweep half-circle right, sweep half-circle back.
  return [
    `M ${cx} ${cy - rOuter}`,
    `A ${rOuter} ${rOuter} 0 1 1 ${cx} ${cy + rOuter}`,
    `A ${rOuter} ${rOuter} 0 1 1 ${cx} ${cy - rOuter}`,
    `M ${cx} ${cy - rInner}`,
    `A ${rInner} ${rInner} 0 1 0 ${cx} ${cy + rInner}`,
    `A ${rInner} ${rInner} 0 1 0 ${cx} ${cy - rInner}`,
    "Z",
  ].join(" ");
}

export function DonutChart({
  data,
  outerRadius = 55,
  innerRadius = 38,
  paddingAngle = 0.04,
  emptyColor = "#E5E7EB",
}: DonutChartProps) {
  const titleId = useId();
  // Filter out non-positive segments — they have no area to render and
  // would produce zero-angle arcs (visually invisible but a bit of dead
  // SVG in the DOM).
  const segments = data.filter((d) => d.value > 0);
  const total = segments.reduce((sum, d) => sum + d.value, 0);

  // viewBox: square box centred so r=55 has a small margin. cx/cy are
  // in viewBox units; the SVG itself sizes via parent CSS.
  const cx = 60;
  const cy = 60;

  // viewBox + preserveAspectRatio + width/height=100% means parent CSS
  // sizes the chart. The previous Recharts <ResponsiveContainer> did
  // the same thing.
  const svgProps = {
    viewBox: "0 0 120 120",
    width: "100%",
    height: "100%",
    role: "img",
    "aria-labelledby": titleId,
  } as const;

  // Empty state: a single grey ring matches the previous Recharts
  // fallback that drew a placeholder Pie when total === 0.
  if (total <= 0) {
    return (
      <svg {...svgProps}>
        <title id={titleId}>No device data</title>
        <path d={fullRingPath(cx, cy, outerRadius, innerRadius)} fill={emptyColor} />
      </svg>
    );
  }

  // Single-segment 100% case — see fullRingPath comment.
  if (segments.length === 1) {
    return (
      <svg {...svgProps}>
        <title id={titleId}>{`${segments[0].name}: ${segments[0].value}`}</title>
        <path d={fullRingPath(cx, cy, outerRadius, innerRadius)} fill={segments[0].color} />
      </svg>
    );
  }

  // Multi-segment: compute cumulative angles, lay out arcs with
  // half-padding shaved from each edge so visible gaps come out
  // approximately `paddingAngle` wide. The math intentionally mirrors
  // d3-shape's pie generator so the visual is the same as the
  // previous Recharts version.
  const halfPad = paddingAngle / 2;
  let cursor = 0;
  return (
    <svg {...svgProps}>
      <title id={titleId}>{`Device status: ${segments.map((s) => `${s.name} ${s.value}`).join(", ")}`}</title>
      {segments.map((seg) => {
        const sweep = (seg.value / total) * TAU;
        const startAngle = cursor + halfPad;
        const endAngle = cursor + sweep - halfPad;
        cursor += sweep;
        // Skip slivers that the padding ate.
        if (endAngle <= startAngle) return null;
        return (
          <path
            key={seg.name}
            d={arcPath(cx, cy, outerRadius, innerRadius, startAngle, endAngle)}
            fill={seg.color}
          >
            <title>{`${seg.name}: ${seg.value}`}</title>
          </path>
        );
      })}
    </svg>
  );
}
